import { useForm } from "@tanstack/react-form";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { ConsentText } from "@/components/consent-text";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@/components/ui/field";
import { FormError } from "@/components/ui/form-error";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { useApiFormErrors } from "@/lib/api-form-errors";
import {
  consentGrants,
  readAcceptance,
  recordablePageUrl,
} from "@/lib/consent";
import { signUp, type SignUpResponse } from "@/lib/data";
import { postEmbedSubmission, useEmbedAutoHeight } from "@/lib/embed";
import { useOrganization } from "@/lib/use-organization";
import { compose, matches, required } from "@/lib/validators";

// Deliberately loose — the authoritative check is Rails' URI::MailTo::EMAIL_REGEXP
// on the way in. This just spares the applicant a round trip for an obvious typo.
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const emailValidator = compose(
  required("Email Address is required"),
  matches(EMAIL_PATTERN, "Email Address is invalid"),
);

const consentValidator = ({ value }: { value: boolean }) =>
  value ? undefined : { message: "Agree to the terms to continue" };

type SignUpSearch = {
  user_identifier?: string;
  sales_zone_identifier?: string;
  page_url?: string;
};

export const Route = createFileRoute("/sign_up")({
  component: EmbeddedSignUpPage,
  // embed.js appends the dealer's rep / sales-zone identifiers to the iframe src.
  // They're the attribution for the lead, so the URL is the only source here —
  // deliberately not the stored referral, which belongs to whoever last used this
  // browser on the portal itself, not to the page hosting the iframe.
  validateSearch: (search: Record<string, unknown>): SignUpSearch => {
    const read = (key: string) =>
      typeof search[key] === "string" ? search[key] : "";

    let user = read("user_identifier");
    let salesZone = read("sales_zone_identifier");

    // Older copies of embed.js appended both identifiers with a leading "?", so a
    // dealer setting both got `?user_identifier=a?sales_zone_identifier=b` — one
    // parameter whose value swallowed the other. Fixed at the source, but dealers
    // embed that script by URL and it is served with a year-long cache, so the old
    // shape keeps arriving. Unpick it rather than drop the sales zone.
    const stacked = user.indexOf("?");
    if (stacked !== -1) {
      const trailing = new URLSearchParams(user.slice(stacked + 1));
      user = user.slice(0, stacked);
      salesZone = salesZone || (trailing.get("sales_zone_identifier") ?? "");
    }

    return {
      ...(user ? { user_identifier: user } : {}),
      ...(salesZone ? { sales_zone_identifier: salesZone } : {}),
      // The dealer page the form is embedded in, which embed.js passes along: the page the
      // Applicant agreed on. Set even when refused, since the router lays this over the raw
      // search and an omitted key would let the refused value through.
      page_url: recordablePageUrl(read("page_url")),
    };
  },
});

function EmbeddedSignUpPage() {
  useEmbedAutoHeight();

  const organization = useOrganization();
  const { user_identifier, sales_zone_identifier, page_url } =
    Route.useSearch();
  const consentTextRef = useRef<HTMLSpanElement>(null);
  const [submitted, setSubmitted] = useState<{
    fullName: string;
    response: SignUpResponse;
  } | null>(null);
  const errors = useApiFormErrors();

  const form = useForm({
    defaultValues: {
      full_name: "",
      phone_number: "",
      email_address: "",
      consent: false,
    },
    onSubmit: async ({ value: { consent, ...details } }) => {
      try {
        const response = await signUp({
          ...details,
          ...(user_identifier ? { user_identifier } : {}),
          ...(sales_zone_identifier ? { sales_zone_identifier } : {}),
          ...(consent && consentTextRef.current
            ? {
                consents: [
                  readAcceptance(
                    consentTextRef.current,
                    consentGrants(organization),
                    page_url,
                  ),
                ],
              }
            : {}),
        });

        setSubmitted({ fullName: details.full_name.trim(), response });
        postEmbedSubmission(response);
      } catch (err: unknown) {
        await errors.handle(form, err);
      }
    },
  });

  if (submitted) {
    return <ThankYou {...submitted} />;
  }

  return (
    <div className="p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <FormError message={errors.baseError} />

        {/* Two columns from 640px up, which is the iframe's own width — it fills
            the dealer's container, so the breakpoint tracks the space we actually
            have rather than the visitor's screen. */}
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <form.Field
            name="full_name"
            validators={{
              onBlur: required("Full Name is required"),
              onSubmit: required("Full Name is required"),
            }}
          >
            {(field) => (
              <Field data-invalid={field.state.meta.errors.length > 0}>
                <FieldLabel htmlFor={field.name}>Full Name</FieldLabel>
                <FieldContent>
                  <Input
                    id={field.name}
                    name={field.name}
                    autoComplete="name"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </FieldContent>
              </Field>
            )}
          </form.Field>

          <form.Field
            name="phone_number"
            validators={{
              onBlur: required("Phone Number is required"),
              onSubmit: required("Phone Number is required"),
            }}
          >
            {(field) => (
              <Field data-invalid={field.state.meta.errors.length > 0}>
                <FieldLabel htmlFor={field.name}>Phone Number</FieldLabel>
                <FieldContent>
                  <PhoneInput
                    id={field.name}
                    name={field.name}
                    placeholder="555-123-4567"
                    value={field.state.value}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </FieldContent>
              </Field>
            )}
          </form.Field>

          <form.Field
            name="email_address"
            validators={{ onBlur: emailValidator, onSubmit: emailValidator }}
          >
            {(field) => (
              <Field
                className="sm:col-span-2"
                data-invalid={field.state.meta.errors.length > 0}
              >
                <FieldLabel htmlFor={field.name}>Email Address</FieldLabel>
                <FieldContent>
                  <Input
                    id={field.name}
                    name={field.name}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </FieldContent>
              </Field>
            )}
          </form.Field>

          <form.Field
            name="consent"
            validators={{ onSubmit: consentValidator }}
          >
            {(field) => (
              <Field
                orientation="horizontal"
                className="sm:col-span-2"
                data-invalid={field.state.meta.errors.length > 0}
              >
                <FieldLabel className="items-start gap-3 font-normal">
                  <Checkbox
                    checked={field.state.value}
                    onCheckedChange={(checked) =>
                      field.handleChange(checked === true)
                    }
                    aria-invalid={field.state.meta.errors.length > 0}
                    className="mt-0.5"
                  />
                  <span className="text-xs leading-relaxed text-muted-foreground">
                    <ConsentText ref={consentTextRef} />
                    <FieldError errors={field.state.meta.errors} />
                  </span>
                </FieldLabel>
              </Field>
            )}
          </form.Field>

          <div className="flex justify-end sm:col-span-2">
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <Button
                  type="submit"
                  size="lg"
                  className="w-full sm:w-auto"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Get Started"
                  )}
                </Button>
              )}
            </form.Subscribe>
          </div>
        </div>
      </form>
    </div>
  );
}

function ThankYou({
  fullName,
  response,
}: {
  fullName: string;
  response: SignUpResponse;
}) {
  return (
    <div className="p-4 text-center">
      <h1 className="font-heading text-xl font-medium text-foreground">
        Thank You{fullName ? ` ${fullName}` : ""}
      </h1>

      <p className="mt-1 text-sm text-foreground">
        You're one step closer to getting a decision.
      </p>

      {/* The host script normally sends the top-level window to this same URL the
          moment it gets our submission message. This link is the fallback for when
          it can't — an old embed snippet, a blocked listener — so it has to break
          out of the frame itself. */}
      <div className="mt-6">
        <Button asChild size="lg">
          <a href={response.verification_url} target="_top">
            Continue Your Application
          </a>
        </Button>
      </div>
    </div>
  );
}
